import React, { useEffect, useMemo, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import products, { categories, policies } from "./data/wholesaleProducts";
import Home from "./components/Home";
import Product from "./components/product";
import ProductDetail from "./components/Productdetail";
import Cart from "./components/Cart";
import Checkout from "./components/Checkout";
import Policy from "./components/Privacy";
import FooterBar from "./components/common/FooterBar";

const categorySlug = (name) => name.toLowerCase().replace(/\s+/g, "-");
const productSlug = (item) =>
  item.images[0]
    .split("/")
    .pop()
    .replace(/\.png$/, "");

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [query] = useState("");
  const [sort, setSort] = useState("featured");
  const [selectedSize, setSelectedSize] = useState({});
  const [navOpen, setNavOpen] = useState(false);
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("delmorian-cart") || "[]");
    } catch {
      return [];
    }
  });

  useEffect(
    () => localStorage.setItem("delmorian-cart", JSON.stringify(cart)),
    [cart],
  );
  useEffect(() => {
    setNavOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const go = (path) => {
    const aliases = {
      home: "/",
      shop: "/shop",
      cart: "/cart",
      checkout: "/checkout",
      policy: "/policies/shipping",
      shipping: "/policies/shipping",
      returns: "/policies/returns",
    };
    navigate(aliases[path] || path);
  };
  const shop = (category = "All") =>
    navigate(category === "All" ? "/shop" : `/shop/${categorySlug(category)}`);
  const openProduct = (item) => navigate(`/products/${productSlug(item)}`);
  const openPolicy = (key) => navigate(`/policies/${key}`);
  const count = cart.reduce((total, item) => total + item.qty, 0);
  const subtotal = cart.reduce(
    (total, item) => total + item.price * item.qty,
    0,
  );

  const ShopPage = () => {
    const { categorySlug: routeCategory } = useParams();
    const category =
      categories.find((item) => categorySlug(item) === routeCategory) || "All";
    const shown = useMemo(() => {
      let list = products.filter(
        (item) =>
          (category === "All" || item.category === category) &&
          `${item.title} ${item.category} ${item.tags.join(" ")}`
            .toLowerCase()
            .includes(query.toLowerCase()),
      );
      if (sort === "low") list = [...list].sort((a, b) => a.price - b.price);
      if (sort === "high") list = [...list].sort((a, b) => b.price - a.price);
      if (sort === "new") list = [...list].reverse();
      return list;
    }, [category, query]);
    return (
      <Product
        items={shown}
        category={category}
        setCategory={shop}
        sort={sort}
        setSort={setSort}
        openProduct={openProduct}
      />
    );
  };

  const DetailPage = () => {
    const { productSlug: routeProductSlug } = useParams();
    const product = products.find(
      (item) => productSlug(item) === routeProductSlug,
    );
    if (!product) return <Navigate to="/shop" replace />;
    const size = selectedSize[product.id] || product.sizes[0];
    const setSize = (nextSize) =>
      setSelectedSize((current) => ({ ...current, [product.id]: nextSize }));
    const add = () =>
      setCart((items) => {
        const key = `${product.id}-${size}`;
        const found = items.find((item) => item.key === key);
        return found
          ? items.map((item) =>
              item.key === key ? { ...item, qty: item.qty + 1 } : item,
            )
          : [...items, { ...product, key, size, qty: 1 }];
      });
    return (
      <ProductDetail
        product={product}
        size={size}
        setSize={setSize}
        add={add}
        shop={shop}
        openProduct={openProduct}
      />
    );
  };

  const PolicyPage = () => {
    const { policyKey } = useParams();
    return policies[policyKey] ? (
      <Policy active={policyKey} openPolicy={openPolicy} />
    ) : (
      <Navigate to="/policies/shipping" replace />
    );
  };

  const routes = (
    <Routes>
      <Route
        path="/"
        element={
          <Home shop={shop} openProduct={openProduct} openPolicy={openPolicy} />
        }
      />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/shop/:categorySlug" element={<ShopPage />} />
      <Route path="/products/:productSlug" element={<DetailPage />} />
      <Route path="/product" element={<Navigate to="/shop" replace />} />
      <Route
        path="/cart"
        element={
          <Cart
            items={cart}
            setCart={setCart}
            subtotal={subtotal}
            shop={shop}
            go={go}
          />
        }
      />
      <Route
        path="/checkout"
        element={
          <Checkout
            items={cart.map((item) => ({ ...item, quantity: item.qty }))}
            onNavigate={go}
          />
        }
      />
      <Route path="/policies/:policyKey" element={<PolicyPage />} />
      <Route
        path="/shipping"
        element={<Navigate to="/policies/shipping" replace />}
      />
      <Route
        path="/return"
        element={<Navigate to="/policies/returns" replace />}
      />
      <Route
        path="/privacy"
        element={<Navigate to="/policies/privacy" replace />}
      />
      <Route
        path="/terms"
        element={<Navigate to="/policies/terms" replace />}
      />
      <Route
        path="/cookie"
        element={<Navigate to="/policies/cookies" replace />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  if (/^\/checkout\/?$/.test(location.pathname))
    return <div className="lunea-shell checkout-only">{routes}</div>;
  return (
    <div className="lunea-shell">
      <div className="announcement">
        FREE DELIVERY FROM $75 <span>•</span> MADE WITH CARE FOR EVERY CHAPTER
      </div>
      <header className="site-header">
        <button
          className="menu-button"
          onClick={() => setNavOpen(!navOpen)}
          aria-label="Toggle menu"
        >
          <i />
          <i />
        </button>
        <nav className={navOpen ? "open" : ""}>
          {categories.slice(1, 4).map((item) => (
            <button key={item} onClick={() => shop(item)}>
              {item}
            </button>
          ))}
        </nav>
        <button className="logo uppercase" onClick={() => go("home")}>
          delmorian<small>CARE FOR GROWING FAMILIES</small>
        </button>
        <div className="header-actions">
          <button onClick={() => go("cart")}>
            BAG <b>{count}</b>
          </button>
        </div>
      </header>
      <div className="subnav">
        {categories.slice(1).map((item) => (
          <button key={item} onClick={() => shop(item)}>
            {item}
          </button>
        ))}
        <button onClick={() => openPolicy("ingredients")}>Our standards</button>
      </div>
      {routes}
      <FooterBar shop={shop} openPolicy={openPolicy} go={go} />
    </div>
  );
}
